/**
 * AppointmentDrawer.js
 * ─────────────────────────────────────────────────────────────────────────────
 * Drawer lateral direito para criar novo agendamento.
 * Formulário compacto com: paciente, profissional, procedimento, sala,
 * duração, valor, status e toggle de WhatsApp.
 *
 * No modo recorrente, o drawer exibe duas colunas:
 *  - Esquerda: formulário de criação
 *  - Direita: painel de resumo da recorrência (RecurrenceSummaryPanel)
 *
 * @module AppointmentDrawer
 */

import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { DataProviderInstance } from '../../../../../api/DataProvider';
import {
  Scheduled,
  Confirmed,
  Rescheduled,
} from '../../../../../consts/AppointmentsState';
import { UpdateAppointmentStatusAPI, MoveAppointmentPaymentAPI } from '../../../../../api/API';
import { isSlotAvailable, minutesToDuration, formatPrice } from '../../utils/appointmentUtils';
import {
  ATTENDANCE_IN_PERSON, ATTENDANCE_ONLINE, ATTENDANCE_MODE_OPTIONS,
  normalizeAttendanceMode, isValidMeetingUrl, MEETING_URL_ERROR,
} from '../../utils/attendanceMode';
import {
  BILLING_PRIVATE, BILLING_INSURANCE, normalizeBillingType, INSURANCE_CARD_REQUIRED_ERROR,
} from '../../utils/billingType';
import CoverageSection from './CoverageSection';
import AppointmentMedicalRequestField, { linkRequestToAppointments } from './AppointmentMedicalRequestField';
import AppointmentAuthorizationField, { isDateCoveredByAuthorization } from './AppointmentAuthorizationField';
import usePatientInsuranceDocuments from './usePatientInsuranceDocuments';
import ProcedureMultiSelect from '../ProcedureMultiSelect/ProcedureMultiSelect';
import InsuranceProcedureLines from '../InsuranceProcedureLines/InsuranceProcedureLines';
import {
  lineFromProcedure,
  linesFromAppointment,
  linesToApi,
  linesTotals,
} from '../ProcedureMultiSelect/procedureLines';
import { SendWhatsAppMessage, SendWhatsAppMessageToWindow } from '../../../../../api/WhatsappAPI';
import MessagesTemplate from '../../../../../consts/MessagesTemplate';
import { MONTHS } from '../../constants/calendarConstants';
import { sendAppointmentCreatedWhatsApp } from '../../../../../utils/appointmentWhatsAppUtils';
import { useCanSendAgendaWhatsApp } from '../../hooks/useAgendaWhatsAppSend';
import usePatientUpcomingAppointments from '../../hooks/usePatientUpcomingAppointments';
import { resolveRoomFor, toDayKey, toTimeKey } from '../../../../../components/day-rooms/dayRooms';
import DrawerHeader from './DrawerHeader';
import PatientSearch from './PatientSearch';
import UpcomingAppointmentNotice, { formatUpcomingDate, sameProfessionalUpcoming, overlappingUpcoming } from './UpcomingAppointmentNotice';
import useAppointmentConflicts, { describeConflict, uniqueConflicts } from './useAppointmentConflicts';
import ConfirmDialog from '../../../../../components/ConfirmDialog/ConfirmDialog';
import { getCurrentUser } from '../../../../../utils/RoleManager';
import RecurrenceSection, { APPOINTMENT_MODE } from './RecurrenceSection';
import RecurrenceSummaryPanel from './RecurrenceSummaryPanel';
import {
  DEFAULT_INTERVAL_DAYS,
  DEFAULT_OCCURRENCES,
  OCCURRENCE_STATUS,
  buildItemsToCreate,
  buildRecurringConfirmationMessage,
  formatDateKey,
  formatTimeKey,
  initOccurrenceDecisions,
  summarizeOccurrences,
  validateRecurrenceConfig,
} from '../../utils/recurrenceUtils';
import CreatePatientPopup from '../../../../../popup/CreatePatientPopup/CreatePatientPopup';
import { isWithinResourceHours } from '../../../../../utils/resourceSchedule';
import styles from './AppointmentDrawer.module.css';

// Chave de localStorage para persistir preferência de WhatsApp (mesma do fluxo antigo)
const KEY_SEND_WHATSAPP = 'sendAppointmentCreationWhatssappReminder';

// ── Ícones SVG inline ─────────────────────────────────────────────────────────

const WhatsAppIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
  </svg>
);

// ── Opções de status inicial ──────────────────────────────────────────────────
// Nota: DURATION_OPTIONS removido — a duração é a soma das linhas de
// procedimento (ProcedureMultiSelect), editável linha a linha.

const STATUS_OPTIONS = [
  { value: Scheduled, label: 'Agendado' },
  { value: Confirmed, label: 'Confirmado' },
];

/**
 * Formata data e hora do slot para o formato da API: "YYYY-MM-DD HH:mm:ss"
 *
 * @param {Date} date
 * @param {number} hour
 * @param {number} minute
 * @returns {string}
 */
function formatDateForAPI(date, hour, minute) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  const h = String(hour).padStart(2, '0');
  const min = String(minute).padStart(2, '0');
  return `${y}-${m}-${d} ${h}:${min}:00`;
}

/**
 * CallPOSTAPI não chama o callback em queda de rede / timeout (10 s): o limite
 * garante que o drawer nunca fica preso em "Salvando...". Sem resposta = null.
 */
function callWithTimeout(call, done, ms = 12000) {
  let settled = false;
  const settle = (response) => {
    if (settled) return;
    settled = true;
    clearTimeout(giveUp);
    done(response);
  };
  const giveUp = setTimeout(() => settle(null), ms);
  call(settle);
}

/**
 * Drawer de criação de novo agendamento.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Se o drawer está aberto
 * @param {Object} props.slot - { date: Date, hour: number, minute: number }
 * @param {Array} props.appointments - Agendamentos existentes (para verificar disponibilidade)
 * @param {Function} props.onClose - Fechar drawer
 * @param {Function} props.onSave - (appointmentData) => void — chamado após salvar
 * @param {Function} [props.onSaveSuccess] - Callback após salvar com sucesso
 * @param {Function} [props.onRecurringModeChange] - (isRecurring: boolean) => void
 * @param {Object} [props.initialValues] - { patient, resourceId, procedureId, procedures, roomId } já preenchidos ao abrir
 *   (reserva cujo telefone já é de um paciente: "Agendar agora"); `procedures` são as
 *   linhas da tela ({ procedure_id, minutes, price }) e prevalecem sobre `procedureId`
 * @returns {React.ReactElement|null}
 */

export default function AppointmentDrawer({
  isOpen,
  slot,
  appointments = [],
  onClose,
  onSave,
  onSaveSuccess,
  onRecurringModeChange,
  onNotifyPatient,
  initialValues = null,
}) {
  const {
    SetAppointment,
    PreviewRecurringAppointments,
    CreateRecurringAppointments,
    LinkAppointmentInsuranceDocument,
    CompanyConfig,
    patients,
    GetPatients,
    GetDayRooms,
  } = DataProviderInstance();

  // Envio pelo sistema (Profissional ou superior / número próprio): a confirmação
  // sai pelo backend depois de salvar, sem abrir o WhatsApp Web.
  const canSendViaSystem = useCanSendAgendaWhatsApp();

  // Ref para acessar a lista de pacientes mais atualizada dentro de callbacks assíncronos
  const patientsRef = useRef(patients);
  useEffect(() => {
    patientsRef.current = patients;
  }, [patients]);

  // ── Estado do formulário ────────────────────────────────────────────────────

  const [createPatientPopupVisible, setCreatePatientPopupVisible] = useState(false);
  const [newPatientSuggestedName, setNewPatientSuggestedName] = useState('');
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [resourceId, setResourceId] = useState('');
  // Linhas de procedimento: [{ procedure_id, minutes, price }]. Duração e valor
  // do agendamento são a soma delas; `procedure` da API é a primeira linha.
  const [procedureLines, setProcedureLines] = useState([]);
  const [roomId, setRoomId] = useState('');
  // Modalidade: presencial (ocupa sala) ou online (link de videochamada opcional)
  const [attendanceMode, setAttendanceMode] = useState(ATTENDANCE_IN_PERSON);
  const [meetingUrl, setMeetingUrl] = useState('');
  const isOnline = attendanceMode === ATTENDANCE_ONLINE;
  // Cobertura: particular ou convênio (carteirinha da pessoa). Só existe na tela
  // quando a empresa tem operadora ativa; sem operadora nada é enviado à API.
  const insuranceProviders = CompanyConfig?.InsuranceProviders || [];
  const hasInsuranceProviders = insuranceProviders.length > 0;
  const [billingType, setBillingType] = useState(BILLING_PRIVATE);
  const [insuranceCardId, setInsuranceCardId] = useState(null);
  const [insuranceProviderId, setInsuranceProviderId] = useState(null);
  const isInsurance = hasInsuranceProviders && billingType === BILLING_INSURANCE;
  // Pedido médico escolhido no drawer: vinculado ao agendamento depois de criado
  const [medicalRequest, setMedicalRequest] = useState(null);
  // Guia escolhida no drawer: vinculada ao agendamento depois de criado, como o pedido
  const [authorization, setAuthorization] = useState(null);
  // Sala pré-preenchida pela sala do dia / sala padrão da agenda (para não
  // sobrescrever o que a pessoa escolheu) e as faixas do dia já buscadas
  const roomAutoRef = useRef('');
  const dayRoomsCacheRef = useRef({ date: null, assignments: [] });
  const [status, setStatus] = useState(Scheduled);
  const procedureTotals = linesTotals(procedureLines);
  const duration = procedureTotals.minutes; // 0 = sem procedimento selecionado
  const price = procedureTotals.price;
  const procedureName = procedureLines[0]?.procedure_id || '';
  const [sendWhatsApp, setSendWhatsApp] = useState(
    () => localStorage.getItem(KEY_SEND_WHATSAPP) === 'true'
  );
  const [isLoading, setIsLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [rooms, setRooms] = useState([]);
  // Paciente já tem agendamento do dia escolhido em diante: reagendar um deles
  // ({type:'reschedule', appointment}) ou manter os dois ({type:'keep'})
  const [upcomingChoice, setUpcomingChoice] = useState(null);
  // Campos copiados do atendimento ao escolher reagendar: { before, applied }
  const rescheduleFillRef = useRef(null);
  // Novo criado, mas o antigo não foi marcado como reagendado: o drawer fica
  // aberto com o aviso e só oferece "Fechar" (salvar de novo duplicaria)
  const [rescheduleFailed, setRescheduleFailed] = useState(false);
  // Choque de horário a confirmar ("Encaixar mesmo assim?"): { conflicts, whatsAppOpened }
  const [conflictPrompt, setConflictPrompt] = useState(null);

  // ── Estado da recorrência ───────────────────────────────────────────────────
  // A recorrência existe apenas durante a criação: nenhum vínculo de série é
  // persistido. Estes estados são descartados ao fechar o drawer.

  const [mode, setMode] = useState(APPOINTMENT_MODE.SINGLE);
  const [intervalDays, setIntervalDays] = useState(DEFAULT_INTERVAL_DAYS);
  const [occurrencesCount, setOccurrencesCount] = useState(DEFAULT_OCCURRENCES);
  const [occurrences, setOccurrences] = useState(null);
  const [isCheckingRecurrence, setIsCheckingRecurrence] = useState(false);
  const [recurrenceError, setRecurrenceError] = useState('');
  const [recurrenceResult, setRecurrenceResult] = useState(null);
  const [pendingConfirmation, setPendingConfirmation] = useState(null);

  // ── Agendamentos já marcados do paciente (só no agendamento único) ─────────

  const selectedPatientId = selectedPatient?.id ?? selectedPatient?.Id ?? null;
  // Registro retroativo (sessão que já aconteceu) não é reagendamento: sem aviso
  const isFutureSlot = Boolean(slot?.date) &&
    new Date(slot.date.getFullYear(), slot.date.getMonth(), slot.date.getDate(),
      slot.hour || 0, slot.minute || 0) > new Date();
  const { upcoming, isLoading: isCheckingUpcoming } = usePatientUpcomingAppointments(
    selectedPatientId,
    slot?.date || null,
    isOpen && mode === APPOINTMENT_MODE.SINGLE && isFutureSlot
  );

  // Trocou o paciente ou o horário: a decisão anterior não vale mais
  const slotKey = slot?.date ? `${slot.date.toDateString()} ${slot.hour}:${slot.minute}` : '';
  useEffect(() => {
    setUpcomingChoice(null);
    rescheduleFillRef.current = null;
  }, [selectedPatientId, slotKey]);

  // Trocou a pessoa: a cobertura volta a Particular; o CoverageSection sugere
  // Convênio + carteirinha quando o último agendamento dela foi por convênio
  useEffect(() => {
    setBillingType(BILLING_PRIVATE);
    setInsuranceCardId(null);
    setMedicalRequest(null);
    setAuthorization(null);
    setErrors(prev => (prev.insuranceCard ? { ...prev, insuranceCard: undefined } : prev));
  }, [selectedPatientId]);

  // Pedidos, guias e exigências das operadoras: uma busca para os blocos Pedido médico e Guia
  const showInsuranceDocuments = isInsurance && selectedPatientId != null && insuranceCardId != null;
  const insuranceDocuments = usePatientInsuranceDocuments(selectedPatientId, showInsuranceDocuments);

  const handleCoverageChange = useCallback(({ billingType: nextType, insuranceCardId: nextCard }) => {
    setBillingType(normalizeBillingType(nextType));
    setInsuranceCardId(nextCard ?? null);
    setErrors(prev => (prev.insuranceCard ? { ...prev, insuranceCard: undefined } : prev));
  }, []);

  const rescheduleTarget = upcomingChoice?.type === 'reschedule' ? upcomingChoice.appointment : null;
  // Mesmo profissional, ou horário que se sobrepõe em qualquer agenda, sem decisão =
  // provável duplicata; outro profissional em outro horário só informa
  const slotStart = slot?.date
    ? new Date(slot.date.getFullYear(), slot.date.getMonth(), slot.date.getDate(), slot.hour || 0, slot.minute || 0)
    : null;
  const overlapping = overlappingUpcoming(upcoming, slotStart, duration);
  const needsUpcomingChoice = !upcomingChoice &&
    (sameProfessionalUpcoming(upcoming, resourceId).length > 0 || overlapping.length > 0);
  // Já decididos no aviso: o servidor não pergunta de novo por eles (nunca dois avisos)
  const acknowledgedIds = useMemo(() => (upcomingChoice?.type === 'reschedule'
    ? [String(upcomingChoice.appointment.id)]
    : upcomingChoice?.type === 'keep'
      ? upcoming.map(a => String(a.id))
      : []), [upcomingChoice, upcoming]);

  // Choque de horário conferido no servidor (agenda, sala cheia, a mesma pessoa)
  const conflicts = useAppointmentConflicts({
    enabled: isOpen && mode === APPOINTMENT_MODE.SINGLE && Boolean(slot?.date) && !rescheduleFailed,
    date: slot?.date ? formatDateForAPI(slot.date, slot.hour, slot.minute) : '',
    duration: minutesToDuration(duration),
    resource: resourceId,
    roomId: isOnline ? null : (roomId || null),
    patient: selectedPatientId,
  });
  const pendingConflicts = conflicts.filter(c => !acknowledgedIds.includes(String(c.id)));
  const roomConflict = pendingConflicts.find(c => c.type === 'room');
  const roomConflictWarning = roomConflict
    ? `${roomConflict.resourceName ? `Sala em uso por ${roomConflict.resourceName}` : 'Sala em uso'} das ${roomConflict.start} às ${roomConflict.end}. Dá para salvar mesmo assim.`
    : '';

  // ── Carrega salas a partir do CompanyConfig (síncrono) ─────────────────────

  useEffect(() => {
    if (!isOpen) return;
    const companyRooms = CompanyConfig?.Rooms || [];
    setRooms(Array.isArray(companyRooms) ? companyRooms : []);
  }, [isOpen, CompanyConfig]);

  // ── Reset ao abrir ──────────────────────────────────────────────────────────

  // Persiste preferência de WhatsApp no localStorage — pula o primeiro render
  // para não sobrescrever o valor salvo antes de o usuário interagir com o toggle.
  const isMountedRef = useRef(false);
  useEffect(() => {
    if (!isMountedRef.current) {
      isMountedRef.current = true;
      return;
    }
    localStorage.setItem(KEY_SEND_WHATSAPP, String(sendWhatsApp));
  }, [sendWhatsApp]);

  useEffect(() => {
    if (isOpen) {
      setSelectedPatient(null);
      setResourceId('');
      setProcedureLines([]); // duração e valor seguem as linhas
      setRoomId('');
      setAttendanceMode(ATTENDANCE_IN_PERSON);
      setMeetingUrl('');
      setBillingType(BILLING_PRIVATE);
      setInsuranceCardId(null);
      setMedicalRequest(null);
      setAuthorization(null);
      roomAutoRef.current = '';
      dayRoomsCacheRef.current = { date: null, assignments: [] };
      setStatus(Scheduled);
      // Relê a preferência do localStorage ao reabrir (componente pode ter sido remontado)
      setSendWhatsApp(localStorage.getItem(KEY_SEND_WHATSAPP) === 'true');
      setErrors({});
      setIsLoading(false);
      setUpcomingChoice(null);
      rescheduleFillRef.current = null;
      setRescheduleFailed(false);
      setConflictPrompt(null);
      // Reset da recorrência — nada é preservado entre aberturas do drawer
      setMode(APPOINTMENT_MODE.SINGLE);
      setIntervalDays(DEFAULT_INTERVAL_DAYS);
      setOccurrencesCount(DEFAULT_OCCURRENCES);
      setOccurrences(null);
      setRecurrenceError('');
      setRecurrenceResult(null);
      setPendingConfirmation(null);
      setIsCheckingRecurrence(false);
    }
  }, [isOpen]);

  // ── Dados do DataProvider ───────────────────────────────────────────────────
  // CompanyConfig usa campos com maiúscula: Id, Label, Duration, Price, Active

  const activeResources = (CompanyConfig?.Resources || []).filter(r => r.Active == null || r.Active == 1);
  // "Só os meus pacientes": a pessoa só agenda na própria agenda, já escolhida
  // (a auto-seleção de agenda única abaixo preenche o campo), como na reserva e no bloqueio
  const currentUser = getCurrentUser();
  const ownResourceId = currentUser?.isHealthProfessional && currentUser?.scheduleScope === 'own' && currentUser?.resourceId != null
    ? String(currentUser.resourceId)
    : null;
  const ownResources = ownResourceId ? activeResources.filter(r => String(r.Id) === ownResourceId) : [];
  const resources = ownResources.length > 0 ? ownResources : activeResources;

  // Fora do horário próprio do profissional: só avisa, não impede salvar
  const selectedResourceSchedule = resources.find(r => String(r.Id) === String(resourceId))?.Schedule;
  const resourceHoursWarning = slot?.date && isWithinResourceHours(
    selectedResourceSchedule,
    slot.date,
    `${String(slot.hour || 0).padStart(2, '0')}:${String(slot.minute || 0).padStart(2, '0')}`,
    duration,
  ) === false
    ? 'Fora do horário deste profissional. Dá para salvar mesmo assim.'
    : '';
  const procedures = (CompanyConfig?.Procedures || []).filter(p => p.Active == null || p.Active == 1);

  // ── Auto-preenchimento ao selecionar profissional ───────────────────────────

  const handleResourceChange = useCallback((e) => {
    const id = e.target.value;
    setResourceId(id);

    // Auto-preenche procedimento padrão do profissional, se disponível
    const resource = resources.find(r => String(r.Id) === String(id));
    const defProc = resource?.DefaultProcedure || resource?.defaultProcedure;
    if (defProc) {
      const proc = procedures.find(p => String(p.Id) === String(defProc));
      // Duração e valor vêm do cadastro do procedimento (linha editável)
      if (proc) setProcedureLines([lineFromProcedure(proc)]);
    }
  }, [resources, procedures]);

  // ── Sala do dia (RECEP-01): pré-preenche a sala pela agenda, dia e horário ──
  // Faixa do dia > sala padrão da agenda > nenhuma. Só mexe quando o campo ainda
  // está vazio ou com o valor que este mesmo preenchimento colocou: o que a pessoa
  // escolher (ou vier da reserva e do reagendamento) fica.

  useEffect(() => {
    if (!isOpen || !resourceId || !slot?.date || isOnline) return undefined;
    const dayKey = toDayKey(slot.date);
    const time = toTimeKey(slot.hour, slot.minute);
    let active = true;
    const apply = (assignments) => {
      if (!active) return;
      const resource = resources.find(r => String(r.Id) === String(resourceId));
      let next = resolveRoomFor(assignments, resource, time).roomId;
      if (next && !(CompanyConfig?.Rooms || []).some(r => String(r.Id) === next)) next = '';
      // O atualizador roda depois desta linha: guarda o valor automático anterior antes de trocar a ref
      const previousAuto = roomAutoRef.current;
      setRoomId(prev => (prev === '' || prev === previousAuto ? next : prev));
      roomAutoRef.current = next;
    };
    if (dayRoomsCacheRef.current.date === dayKey) {
      apply(dayRoomsCacheRef.current.assignments);
    } else {
      GetDayRooms(dayKey, (result) => {
        const assignments = result?.status === 'SUCCESS' ? (result.data?.assignments || []) : [];
        dayRoomsCacheRef.current = { date: dayKey, assignments };
        apply(assignments);
      });
    }
    return () => { active = false; };
  }, [isOpen, resourceId, slotKey, isOnline]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Seleção de um procedimento só (auto-seleção, initialValues, reagendar) ──

  const selectSingleProcedure = useCallback((procedureId) => {
    const proc = procedures.find(p => String(p.Id) === String(procedureId));
    if (proc) setProcedureLines([lineFromProcedure(proc)]);
  }, [procedures]);

  // ── Auto-seleção quando há apenas um profissional disponível ───────────────

  useEffect(() => {
    if (!isOpen) return;
    if (resources.length === 1 && !resourceId) {
      handleResourceChange({ target: { value: String(resources[0].Id) } });
    }
  }, [isOpen, resources, handleResourceChange]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Auto-seleção quando há apenas um procedimento disponível ───────────────

  useEffect(() => {
    if (!isOpen) return;
    if (procedures.length === 1 && !procedureName) {
      selectSingleProcedure(procedures[0].Id);
    }
  }, [isOpen, procedures, selectSingleProcedure]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Pré-preenchimento (initialValues) ──────────────────────────────────────

  useEffect(() => {
    if (!isOpen || !initialValues) return;
    if (initialValues.patient) setSelectedPatient(initialValues.patient);
    if (initialValues.resourceId != null && initialValues.resourceId !== '') handleResourceChange({ target: { value: String(initialValues.resourceId) } });
    // Linhas já escolhidas (reserva com vários serviços) ou um procedimento só
    if (Array.isArray(initialValues.procedures) && initialValues.procedures.length > 0) {
      setProcedureLines(initialValues.procedures);
    } else if (initialValues.procedureId) {
      selectSingleProcedure(initialValues.procedureId);
    }
    if (initialValues.roomId) setRoomId(String(initialValues.roomId));
  }, [isOpen, initialValues]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── Reagendar: copia profissional, procedimento e sala do atendimento ──────
  // O preenchimento acontece uma vez, no clique. O que a pessoa mudar depois
  // fica; "Desfazer" só devolve os campos que ainda estão com o valor copiado.

  const restoreRescheduleFill = useCallback(() => {
    const fill = rescheduleFillRef.current;
    rescheduleFillRef.current = null;
    if (!fill) return;
    const { before, applied } = fill;
    if ('resourceId' in applied && resourceId === applied.resourceId) setResourceId(before.resourceId);
    if ('procedureLines' in applied && JSON.stringify(procedureLines) === applied.procedureLines) {
      setProcedureLines(before.procedureLines);
    }
    if ('roomId' in applied && roomId === applied.roomId) setRoomId(before.roomId);
    if ('attendanceMode' in applied && attendanceMode === applied.attendanceMode) setAttendanceMode(before.attendanceMode);
    if ('meetingUrl' in applied && meetingUrl === applied.meetingUrl) setMeetingUrl(before.meetingUrl);
    if ('billingType' in applied && billingType === applied.billingType
      && String(insuranceCardId ?? '') === String(applied.insuranceCardId ?? '')) {
      setBillingType(before.billingType);
      setInsuranceCardId(before.insuranceCardId);
    }
  }, [resourceId, procedureLines, roomId, attendanceMode, meetingUrl, billingType, insuranceCardId]);

  const applyRescheduleFill = useCallback((apt) => {
    const applied = {};
    const resource = String(apt.resource || '');
    // Todas as linhas do atendimento (ou o procedimento principal, nos antigos)
    const lines = linesFromAppointment(apt, procedures);
    const room = apt.roomId ? String(apt.roomId) : '';
    if (resource && resources.some(r => String(r.Id) === resource)) {
      setResourceId(resource);
      applied.resourceId = resource;
    }
    if (lines.length > 0) {
      setProcedureLines(lines);
      applied.procedureLines = JSON.stringify(lines);
    }
    // Modalidade e link acompanham o atendimento reagendado; online não leva sala
    const mode = normalizeAttendanceMode(apt.attendance_mode);
    const link = mode === ATTENDANCE_ONLINE ? String(apt.meeting_url || '') : '';
    setAttendanceMode(mode);
    applied.attendanceMode = mode;
    setMeetingUrl(link);
    applied.meetingUrl = link;
    if (mode === ATTENDANCE_ONLINE) {
      setRoomId('');
      applied.roomId = '';
    } else if (room && rooms.some(r => String(r.Id || r.id) === room)) {
      setRoomId(room);
      applied.roomId = room;
    }
    // Cobertura acompanha o atendimento reagendado (quem veio por convênio volta por convênio)
    if (hasInsuranceProviders && apt.billing_type) {
      const type = normalizeBillingType(apt.billing_type);
      const card = type === BILLING_INSURANCE && apt.patient_insurance_id ? apt.patient_insurance_id : null;
      setBillingType(type);
      setInsuranceCardId(card);
      applied.billingType = type;
      applied.insuranceCardId = card;
    }
    rescheduleFillRef.current = {
      before: { resourceId, procedureLines, roomId, attendanceMode, meetingUrl, billingType, insuranceCardId },
      applied,
    };
  }, [resources, procedures, rooms, resourceId, procedureLines, roomId, attendanceMode, meetingUrl,
    hasInsuranceProviders, billingType, insuranceCardId]);

  // Presencial -> online solta a sala; online -> presencial esquece o link (não volta a sala antiga)
  const handleAttendanceModeChange = useCallback((mode) => {
    setAttendanceMode(mode);
    if (mode === ATTENDANCE_ONLINE) setRoomId('');
    else setMeetingUrl('');
    setErrors(prev => ({ ...prev, meetingUrl: undefined }));
  }, []);

  const handleUpcomingChoose = useCallback((choice) => {
    if (rescheduleTarget) restoreRescheduleFill();
    if (choice?.type === 'reschedule') applyRescheduleFill(choice.appointment);
    setUpcomingChoice(choice);
    setErrors(prev => ({ ...prev, upcoming: undefined }));
  }, [rescheduleTarget, restoreRescheduleFill, applyRescheduleFill]);

  // ── Validação ───────────────────────────────────────────────────────────────

  const validate = useCallback(() => {
    const errs = {};
    if (!selectedPatient) errs.patient = 'Selecione um paciente';
    if (!resourceId) errs.resource = 'Selecione uma agenda';
    if (procedureLines.length === 0) errs.procedure = 'Selecione pelo menos um procedimento';
    if (isOnline && !isValidMeetingUrl(meetingUrl)) errs.meetingUrl = MEETING_URL_ERROR;
    if (isInsurance && !insuranceCardId) errs.insuranceCard = INSURANCE_CARD_REQUIRED_ERROR;
    return errs;
  }, [selectedPatient, resourceId, procedureLines, isOnline, meetingUrl, isInsurance, insuranceCardId]);

  // Campos de cobertura do POST: só quando a empresa trabalha com convênio
  // (sem eles o backend assume particular). Em particular o id vai vazio.
  const coverageFields = useMemo(() => (hasInsuranceProviders
    ? { billing_type: isInsurance ? BILLING_INSURANCE : BILLING_PRIVATE, patient_insurance_id: isInsurance ? insuranceCardId : null }
    : {}), [hasInsuranceProviders, isInsurance, insuranceCardId]);

  /**
   * Depois de criar o novo agendamento no lugar de um já existente: marca o
   * antigo como reagendado (libera o horário e cancela as mensagens pendentes
   * dele na fila do WhatsApp) e, se ele já estava pago, passa o pagamento para
   * o novo. Mesmo resultado de handleConfirmReschedule.
   *
   * done(result): 'ok' | 'status_failed' | 'payment_failed'
   */
  const markOriginalRescheduled = useCallback((original, createdId, done) => {
    // Só o status muda: o antigo fica com a data e os dados de sempre
    callWithTimeout(
      (cb) => UpdateAppointmentStatusAPI(original.id, Rescheduled, cb),
      (response) => {
        if (response?.status !== 'SUCCESS') {
          done('status_failed');
          return;
        }
        if (!original.paid) {
          done('ok');
          return;
        }
        callWithTimeout(
          (cb) => MoveAppointmentPaymentAPI(original.id, createdId, cb),
          (moved) => {
            done(moved?.status === 'SUCCESS' ? 'ok' : 'payment_failed');
          },
        );
      },
    );
  }, []);

  // ── Salvar ──────────────────────────────────────────────────────────────────

  // options: { force } encaixa mesmo com choque; { skipWhatsApp } o WhatsApp Web já abriu
  const handleSave = useCallback(async (options) => {
    const force = options?.force === true;
    const errs = validate();
    if (needsUpcomingChoice) {
      errs.upcoming = 'Escolha se quer reagendar o atendimento marcado ou manter os dois.';
    }
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    // Choque já conhecido: pergunta antes, ainda no clique (o WhatsApp Web só abre nele)
    if (!force && pendingConflicts.length > 0) {
      setConflictPrompt({ conflicts: pendingConflicts, whatsAppOpened: false });
      return;
    }

    setIsLoading(true);
    setErrors({});

    // A API espera o ID numérico do paciente (não o nome)
    const patientId = selectedPatient?.id || selectedPatient?.Id || null;
    const patientPhone = selectedPatient?.phone || selectedPatient?.Phone ||
                         selectedPatient?.cellphone || selectedPatient?.Cellphone || null;

    // Linhas de procedimento no formato da API; duração e valor são a soma delas
    const apiLines = linesToApi(procedureLines);
    const rawDuration = minutesToDuration(duration);
    const rawPrice = price;

    const appointmentData = {
      status,
      date: formatDateForAPI(slot.date, slot.hour, slot.minute),
      patient: patientId,         // ID numérico do paciente (obrigatório pela API)
      resource: resourceId,       // Id do profissional (string)
      procedure: procedureName,   // Id do procedimento principal (primeira linha)
      duration: rawDuration,      // Soma das linhas ("HH:MM:SS")
      price: rawPrice,            // Soma das linhas
      procedures: apiLines,       // [{ procedure_id, duration, price }]
      companyId: CompanyConfig?.Id || CompanyConfig?.id || '',
      roomId: isOnline ? null : (roomId || null),
      attendance_mode: attendanceMode,
      meeting_url: isOnline ? (meetingUrl.trim() || null) : null,
      ...coverageFields,
      phone: patientPhone,
      // O servidor confere o choque de novo; quem confirmou encaixa mesmo assim
      checkConflicts: true,
      conflictConfirmed: force,
      conflictAcknowledged: acknowledgedIds,
    };

    // ── Abre o WhatsApp IMEDIATAMENTE no clique (antes do callback da API) ──────
    // window.open só é permitido pelo browser em resposta direta a evento de usuário.
    // Fazer isso dentro de um callback assíncrono (resposta de API) causa bloqueio de popup.
    // Com envio pelo sistema o aviso sai depois de salvar (onNotifyPatient).
    const opensWhatsApp = sendWhatsApp && patientPhone && !canSendViaSystem;
    if (opensWhatsApp && !options?.skipWhatsApp) {
      const resource = resources.find(r => String(r.Id) === String(resourceId));
      const h = String(slot?.hour ?? 0).padStart(2, '0');
      const m = String(slot?.minute ?? 0).padStart(2, '0');
      sendAppointmentCreatedWhatsApp({
        patientPhone,
        patientName: selectedPatient?.name || '',
        countryCode: String(selectedPatient?.countryCode || ''),
        aptDate: slot?.date || new Date(),
        time: `${h}:${m}`,
        doctorName: resource?.Label || '',
        price: rawPrice,
        companyConfig: CompanyConfig,
        rescheduled: Boolean(rescheduleTarget),
      });
    }

    SetAppointment(appointmentData, (result) => {
      // Choque que a tela ainda não conhecia (outra pessoa marcou agora): nada foi gravado
      if (result?.status === 'CONFLICT') {
        setIsLoading(false);
        setConflictPrompt({
          conflicts: Array.isArray(result.conflicts) ? result.conflicts : [],
          whatsAppOpened: Boolean(opensWhatsApp) || Boolean(options?.skipWhatsApp),
        });
        return;
      }

      // A API retorna { error: '...' } em caso de falha
      if (result?.error) {
        setIsLoading(false);
        // Carteirinha recusada pelo backend (inativa, de outra pessoa, ausente): aviso ao lado do campo
        const insuranceRefused = String(result.error) === INSURANCE_CARD_REQUIRED_ERROR;
        setErrors({
          general: insuranceRefused ? undefined : 'Erro ao salvar agendamento. Tente novamente.',
          insuranceCard: insuranceRefused ? INSURANCE_CARD_REQUIRED_ERROR : undefined,
        });
        return;
      }

      // Adiciona ID retornado pela API
      const savedAppointment = { ...appointmentData, id: result?.id || result?.Id || null };

      // set_appointment.php devolve { status: "SUCCESS", data: { id, ... } }
      const createdId = result?.data?.id ?? result?.id ?? null;
      if (sendWhatsApp && patientPhone && canSendViaSystem && createdId) {
        onNotifyPatient?.(rescheduleTarget
          ? { type: 'rescheduled', appointment_id: createdId }
          : { type: 'created', appointment_ids: [createdId] });
      }

      const finish = () => {
        setIsLoading(false);
        onSave?.(savedAppointment);
        onSaveSuccess?.();
        onClose?.();
      };

      // Pedido escolhido no drawer: vincula antes de fechar, para os avisos de
      // convênio já virem sem "falta pedido". Falha no vínculo não desfaz o
      // agendamento (o aviso de sempre aparece e o painel permite vincular).
      const linkRequest = (isInsurance && medicalRequest && createdId
        ? linkRequestToAppointments(LinkAppointmentInsuranceDocument, [createdId], medicalRequest.id)
        : Promise.resolve(0))
        .then(() => (isInsurance && authorization && createdId
          ? linkRequestToAppointments(LinkAppointmentInsuranceDocument, [createdId], authorization.id, 'authorization')
          : 0));

      // O antigo só é liberado depois que o novo existe: se a criação falhar,
      // o paciente não fica sem nenhum dos dois
      linkRequest.then(() => {
        if (rescheduleTarget) {
          const oldDate = formatUpcomingDate(rescheduleTarget.date);
          markOriginalRescheduled(rescheduleTarget, createdId, (outcome) => {
            if (outcome === 'ok') {
              finish();
              return;
            }
            setIsLoading(false);
            setRescheduleFailed(true);
            setErrors({
              general: outcome === 'payment_failed'
                ? `Reagendado, mas o pagamento continua no agendamento de ${oldDate}.`
                : `O novo agendamento foi criado, mas o de ${oldDate} continua marcado. ` +
                  'Cancele-o pela agenda para liberar o horário.',
            });
          });
        } else {
          finish();
        }
      });
    });
  }, [
    validate, selectedPatient, resourceId, procedureName, procedureLines, roomId, attendanceMode, meetingUrl, isOnline,
    coverageFields, duration, price, status, slot, CompanyConfig, sendWhatsApp,
    SetAppointment, onSave, onSaveSuccess, onClose, resources,
    canSendViaSystem, onNotifyPatient, needsUpcomingChoice, rescheduleTarget,
    markOriginalRescheduled, isInsurance, medicalRequest, authorization, LinkAppointmentInsuranceDocument,
    pendingConflicts, acknowledgedIds,
  ]);

  const handleConflictConfirm = useCallback(() => {
    const whatsAppOpened = conflictPrompt?.whatsAppOpened;
    setConflictPrompt(null);
    handleSave({ force: true, skipWhatsApp: whatsAppOpened });
  }, [conflictPrompt, handleSave]);

  const handleConflictCancel = useCallback(() => setConflictPrompt(null), []);

  // ── Recorrência ─────────────────────────────────────────────────────────────

  /**
   * Ao trocar de modo, descarta qualquer revisão anterior.
   * O fluxo de agendamento único permanece exatamente como antes.
   */
  const handleModeChange = useCallback((nextMode) => {
    setMode(nextMode);
    setOccurrences(null);
    setRecurrenceError('');
    setRecurrenceResult(null);
    setPendingConfirmation(null);
    // Notifica o pai para expandir/recolher o drawer
    onRecurringModeChange?.(nextMode === APPOINTMENT_MODE.RECURRING);
  }, [onRecurringModeChange]);

  /** Recalcular sempre que a configuração mudar invalida a revisão atual */
  const handleIntervalDaysChange = useCallback((days) => {
    setIntervalDays(days);
    setOccurrences(null);
    setRecurrenceResult(null);
  }, []);

  const handleOccurrencesCountChange = useCallback((count) => {
    setOccurrencesCount(count);
    setOccurrences(null);
    setRecurrenceResult(null);
  }, []);

  /**
   * Calcula as datas e verifica a disponibilidade de cada ocorrência.
   * Nada é criado nesta etapa — apenas monta a revisão.
   */
  const handleCheckRecurrence = useCallback(() => {
    if (!resourceId) {
      setRecurrenceError('Selecione uma agenda para verificar a disponibilidade.');
      return;
    }

    const configError = validateRecurrenceConfig(intervalDays, occurrencesCount);
    if (configError) {
      setRecurrenceError(configError);
      return;
    }

    setRecurrenceError('');
    setRecurrenceResult(null);
    setIsCheckingRecurrence(true);

    PreviewRecurringAppointments(
      {
        resourceId,
        startDate: formatDateKey(slot.date),
        startTime: formatTimeKey(slot.hour, slot.minute),
        intervalDays,
        occurrences: occurrencesCount,
        procedureDuration: duration || undefined,
      },
      (response) => {
        setIsCheckingRecurrence(false);

        if (!response?.success || !response?.occurrences) {
          // O endpoint devolve a mensagem em `error` (string)
          const msg = typeof response?.error === 'string' ? response.error : null;
          setRecurrenceError(msg || 'Não foi possível verificar a disponibilidade.');
          return;
        }

        setOccurrences(initOccurrenceDecisions(response.occurrences));
      }
    );
  }, [
    resourceId, intervalDays, occurrencesCount, slot, duration,
    PreviewRecurringAppointments,
  ]);

  /**
   * Ignora / reinclui uma ocorrência.
   *
   * `skipped` é uma decisão explícita do usuário e vale para qualquer estado.
   * Ao reincluir uma ocorrência disponível, o horário original é restaurado.
   */
  const handleToggleSkip = useCallback((index) => {
    setOccurrences((prev) =>
      (prev || []).map((occ, i) => {
        if (i !== index) return occ;

        const skipped = !occ.skipped;

        // Ao reincluir, devolve o horário original se ele estava disponível
        const selectedTime =
          !skipped && !occ.selectedTime && occ.status === OCCURRENCE_STATUS.AVAILABLE
            ? occ.time
            : occ.selectedTime;

        return { ...occ, skipped, selectedTime };
      })
    );
  }, []);

  /**
   * Escolhe outro horário disponível no mesmo dia para resolver um conflito.
   *
   * Valor vazio devolve a ocorrência ao estado "indisponível" (pendente), e não
   * a "ignorada" — ignorar é uma ação explícita e separada.
   */
  const handleSelectAlternative = useCallback((index, time) => {
    setOccurrences((prev) =>
      (prev || []).map((occ, i) =>
        i === index
          ? { ...occ, selectedTime: time || null }
          : occ
      )
    );
  }, []);

  /**
   * Cria em lote as ocorrências aprovadas.
   *
   * Estratégia: best-effort com relatório explícito (mesma do backend). Uma
   * ocorrência que ficou indisponível entre a revisão e a criação não impede as
   * demais, e o resultado mostra claramente o que foi criado e o que falhou.
   *
   * A confirmação é enviada em UMA única mensagem, contendo somente os
   * agendamentos efetivamente criados.
   *
   * Sobre o WhatsApp: a mensagem só pode ser montada após a resposta (depende de
   * QUAIS datas foram criadas), mas window.open só é permitido durante o clique.
   * Solução: abrimos uma aba EM BRANCO no clique e a redirecionamos quando a
   * resposta chega — mesmo resultado do fluxo único. Se o navegador bloquear a
   * aba, cai no fallback do botão "Enviar confirmação".
   */
  const handleSaveRecurring = useCallback(() => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      return;
    }

    if (!occurrences) {
      setRecurrenceError('Verifique a disponibilidade antes de criar os agendamentos.');
      return;
    }

    const items = buildItemsToCreate(occurrences);
    if (items.length === 0) {
      setRecurrenceError('Nenhuma data selecionada para criação.');
      return;
    }

    setIsLoading(true);
    setErrors({});
    setRecurrenceError('');

    const patientId = selectedPatient?.id || selectedPatient?.Id || null;
    const rawDuration = minutesToDuration(duration);
    const rawPrice = price;

    const patientPhone = selectedPatient?.phone || selectedPatient?.Phone ||
                         selectedPatient?.cellphone || selectedPatient?.Cellphone || null;

    // Abre a aba do WhatsApp AGORA (em branco) — ainda dentro do gesto do
    // usuário, quando o navegador permite. Ela será redirecionada assim que
    // soubermos quais agendamentos foram realmente criados.
    let whatsappWindow = null;
    if (sendWhatsApp && patientPhone && !canSendViaSystem) {
      whatsappWindow = window.open('', '_blank');
    }

    const data = {
      status,
      patient: patientId,
      resource: resourceId,
      procedure: procedureName,
      duration: rawDuration,
      price: rawPrice,
      procedures: linesToApi(procedureLines),
      roomId: isOnline ? null : (roomId || null),
      attendance_mode: attendanceMode,
      meeting_url: isOnline ? (meetingUrl.trim() || null) : null,
      ...coverageFields,
    };

    CreateRecurringAppointments(data, items, (response) => {
      setIsLoading(false);

      const created = response?.created || [];

      // Nada criado (erro ou todos os horários ocupados): não há confirmação a
      // enviar — fecha a aba em branco para não deixar lixo na tela.
      if (response?.error || created.length === 0) {
        if (whatsappWindow && !whatsappWindow.closed) whatsappWindow.close();

        if (response?.error) {
          setErrors({ general: 'Erro ao criar os agendamentos. Tente novamente.' });
          return;
        }

        setRecurrenceResult(response);
        return;
      }

      setRecurrenceResult(response);

      // Mensagem única com apenas os agendamentos efetivamente criados.
      // Pelo sistema: o backend monta a mesma mensagem (com a lista) e envia na hora.
      if (sendWhatsApp && patientPhone && canSendViaSystem) {
        onNotifyPatient?.({ type: 'created', appointment_ids: created.map(c => c.id) });
      } else if (sendWhatsApp && patientPhone) {
        const templates = MessagesTemplate.getTemplates(CompanyConfig);
        const template = templates.AppointmentCreatedFullMessage ||
                         templates.AppointmentCreatedShortMessage || '';

        const resource = resources.find(r => String(r.Id) === String(resourceId));
        const countryCode = selectedPatient?.countryCode || '';

        const rawMessage = buildRecurringConfirmationMessage({
          template,
          items: created.map(c => ({ date: c.date, time: c.time })),
          patientFirstName: (selectedPatient?.name || selectedPatient?.Name || '').split(' ')[0],
          doctorName: resource?.Label || '',
          companyConfig: CompanyConfig,
          price: rawPrice,
          months: MONTHS,
        });

        // Template não configurado → abre o WhatsApp sem texto (igual ao fluxo único)
        const message = rawMessage
          ? MessagesTemplate.ConvertToWhatsAppText(rawMessage)
          : '';

        const sent = SendWhatsAppMessageToWindow(
          whatsappWindow, patientPhone, message, countryCode
        );

        // Aba bloqueada pelo navegador → oferece o envio manual como fallback
        if (!sent) {
          setPendingConfirmation({ phone: patientPhone, countryCode, message });
        }
      }

      // Pedido escolhido: vai para as datas criadas até o vencimento dele
      const requestUntil = medicalRequest?.valid_until ? String(medicalRequest.valid_until).slice(0, 10) : '';
      const coveredIds = created
        .filter(c => !requestUntil || String(c.date || '').slice(0, 10) <= requestUntil)
        .map(c => c.id);
      // Guia escolhida: vai para as datas criadas dentro da validade dela
      const authorizationIds = authorization
        ? created.filter(c => isDateCoveredByAuthorization(authorization, c.date)).map(c => c.id)
        : [];
      const linkRequest = (isInsurance && medicalRequest
        ? linkRequestToAppointments(LinkAppointmentInsuranceDocument, coveredIds, medicalRequest.id)
        : Promise.resolve(0))
        .then(() => (isInsurance && authorization
          ? linkRequestToAppointments(LinkAppointmentInsuranceDocument, authorizationIds, authorization.id, 'authorization')
          : 0));

      linkRequest.then(() => {
        onSave?.({ ...data, recurringCreated: created });
        onSaveSuccess?.();
      });
    });
  }, [
    validate, occurrences, selectedPatient, procedureName, procedureLines, duration,
    price, status, resourceId, roomId, attendanceMode, meetingUrl, isOnline, coverageFields, sendWhatsApp, CompanyConfig, resources,
    CreateRecurringAppointments, onSave, onSaveSuccess, canSendViaSystem, onNotifyPatient,
    isInsurance, medicalRequest, authorization, LinkAppointmentInsuranceDocument,
  ]);

  /**
   * Fallback de envio da confirmação, usado apenas quando o navegador bloqueia a
   * aba aberta durante o clique. A mensagem já vem pronta (e convertida).
   */
  const handleSendConfirmation = useCallback(() => {
    if (!pendingConfirmation) return;

    SendWhatsAppMessage(
      pendingConfirmation.phone,
      pendingConfirmation.message,
      pendingConfirmation.countryCode
    );

    setPendingConfirmation(null);
  }, [pendingConfirmation]);

  const isRecurringMode = mode === APPOINTMENT_MODE.RECURRING;
  const recurrenceSummary = occurrences ? summarizeOccurrences(occurrences) : null;
  const recurringDone = Boolean(recurrenceResult);

  // ── Disponibilidade do slot ─────────────────────────────────────────────────

  // O atendimento que vai ser reagendado sai da conta: trocar 10:00 por 10:30
  // não pode aparecer como conflito com ele mesmo. Só contam os agendamentos da
  // agenda escolhida e os bloqueios gerais (com várias agendas, o horário de uma
  // não ocupa as outras).
  const isAvailable = slot
    ? isSlotAvailable(
      rescheduleTarget
        ? appointments.filter(a => String(a.id) !== String(rescheduleTarget.id))
        : appointments,
      slot.date, slot.hour, slot.minute, duration,
      { resourceId }
    )
    : true;

  if (!isOpen) return null;

  // ── Conteúdo do formulário (coluna esquerda) ────────────────────────────────

  const formContent = (
    <>
      {/* Erro geral */}
      {errors.general && (
        <div className={styles.validationError} role="alert">
          {errors.general}
        </div>
      )}

      {/* ── Paciente ──────────────────────────────────────────────────────── */}
      <div className={styles.formSection}>
        <div className={styles.formSectionHeader}>
          <label className={`${styles.fieldLabel} ${styles.fieldLabelRequired}`}>
            Paciente
          </label>
          <button
            type="button"
            className={styles.newPatientBtn}
            onClick={() => {
              setCreatePatientPopupVisible(true);
            }}
          >
            + Novo paciente
          </button>
        </div>
        <PatientSearch
          patients={patients || []}
          selectedPatient={selectedPatient}
          onSelect={setSelectedPatient}
          onCreateNew={(name) => {
            setNewPatientSuggestedName(name || '');
            setCreatePatientPopupVisible(true);
          }}
          onQueryChange={(q) => setNewPatientSuggestedName(q)}
        />
        {errors.patient && (
          <span className={styles.validationError}>{errors.patient}</span>
        )}
        {!isRecurringMode && (
          <UpcomingAppointmentNotice
            patientName={selectedPatient?.name || selectedPatient?.Name || ''}
            upcoming={upcoming}
            resourceId={resourceId}
            overlapping={overlapping}
            procedures={procedures}
            choice={upcomingChoice}
            onChoose={handleUpcomingChoose}
            error={errors.upcoming}
          />
        )}
      </div>

      {/* ── Modalidade: presencial (sala) ou online (link de videochamada) ──── */}
      <div className={styles.formSection}>
        <label className={styles.fieldLabel}>Modalidade</label>
        <div className={styles.modeSelector} role="radiogroup" aria-label="Modalidade">
          {ATTENDANCE_MODE_OPTIONS.map(option => (
            <button
              key={option.id}
              type="button"
              role="radio"
              aria-checked={attendanceMode === option.id}
              className={`${styles.modeBtn} ${attendanceMode === option.id ? styles.modeBtnActive : ''}`}
              onClick={() => handleAttendanceModeChange(option.id)}
            >
              {option.label}
            </button>
          ))}
        </div>
      </div>

      {/* ── Cobertura: particular ou convênio (só com operadora cadastrada) ──── */}
      {hasInsuranceProviders && (
        <CoverageSection
          patientId={selectedPatientId}
          providers={insuranceProviders}
          billingType={billingType}
          insuranceCardId={insuranceCardId}
          onChange={handleCoverageChange}
          onProviderChange={setInsuranceProviderId}
          error={errors.insuranceCard}
          disabled={isLoading}
        />
      )}

      {/* ── Pedido médico (convênio): escolhido ou registrado aqui, vinculado ao salvar ── */}
      {showInsuranceDocuments && (
        <AppointmentMedicalRequestField
          patientId={selectedPatientId}
          providerId={insuranceProviderId}
          insuranceDocuments={insuranceDocuments}
          appointmentDate={slot?.date || null}
          value={medicalRequest}
          onChange={setMedicalRequest}
          isRecurring={isRecurringMode}
          disabled={isLoading}
        />
      )}

      {/* ── Guia (convênio): escolhida ou registrada aqui, vinculada ao salvar ── */}
      {showInsuranceDocuments && (
        <AppointmentAuthorizationField
          patientId={selectedPatientId}
          providerId={insuranceProviderId}
          cardId={insuranceCardId}
          providers={insuranceProviders}
          insuranceDocuments={insuranceDocuments}
          appointmentDate={slot?.date || null}
          value={authorization}
          onChange={setAuthorization}
          isRecurring={isRecurringMode}
          disabled={isLoading}
        />
      )}

      {/* ── Profissional + Sala (presencial) ou Link da videochamada (online) ── */}
      <div className={styles.formGrid2}>
        <div className={styles.formSection}>
          <label className={`${styles.fieldLabel} ${styles.fieldLabelRequired}`}>
            Agenda
          </label>
          <select
            className={styles.fieldSelect}
            value={resourceId}
            onChange={handleResourceChange}
          >
            <option value="">Selecionar...</option>
            {resources.map(r => (
              <option key={r.Id} value={String(r.Id)}>
                {r.Label}
              </option>
            ))}
          </select>
          {errors.resource && (
            <span className={styles.validationError}>{errors.resource}</span>
          )}
          {!errors.resource && resourceHoursWarning && (
            <span className={styles.fieldWarning}>{resourceHoursWarning}</span>
          )}
        </div>

        {isOnline ? (
          <div className={styles.formSection}>
            <label className={styles.fieldLabel}>Link da videochamada</label>
            <input
              type="url"
              inputMode="url"
              autoComplete="off"
              className={styles.fieldInput}
              placeholder="https://... (opcional)"
              value={meetingUrl}
              onChange={e => {
                setMeetingUrl(e.target.value);
                if (errors.meetingUrl) setErrors(prev => ({ ...prev, meetingUrl: undefined }));
              }}
            />
            {errors.meetingUrl && (
              <span className={styles.validationError}>{errors.meetingUrl}</span>
            )}
          </div>
        ) : (
          <div className={styles.formSection}>
            <label className={styles.fieldLabel}>Sala</label>
            <select
              className={styles.fieldSelect}
              value={roomId}
              onChange={e => setRoomId(e.target.value)}
            >
              <option value="">Sem sala</option>
              {rooms.map(r => (
                <option key={r.Id || r.id} value={String(r.Id || r.id)}>
                  {r.Label || r.label || r.Name || r.name}
                </option>
              ))}
            </select>
            {roomConflictWarning && (
              <span className={styles.fieldWarning}>{roomConflictWarning}</span>
            )}
          </div>
        )}
      </div>

      {/* ── Procedimentos (um ou mais; duração e valor por linha) ──────────── */}
      <div className={styles.formSection}>
        <label className={`${styles.fieldLabel} ${styles.fieldLabelRequired}`}>
          Procedimentos
        </label>
        <ProcedureMultiSelect
          procedures={procedures}
          value={procedureLines}
          onChange={(lines) => {
            setProcedureLines(lines);
            if (lines.length > 0) setErrors(prev => ({ ...prev, procedure: undefined }));
          }}
          error={errors.procedure}
        />
        {isInsurance && insuranceProviderId && (
          <InsuranceProcedureLines
            providerId={insuranceProviderId}
            lines={procedureLines}
            procedures={procedures}
            onChange={setProcedureLines}
            disabled={isLoading}
          />
        )}
      </div>

      {/* ── Duração + Valor (somas das linhas) + Status ────────────────────── */}
      <div className={styles.formGrid3}>
        <div className={styles.formSection}>
          <label className={styles.fieldLabel}>Duração</label>
          <div className={styles.fieldInputReadonly}>
            {procedureName && duration > 0
              ? `${duration} min`
              : procedureName
                ? 'Não definida'
                : '—'}
          </div>
        </div>

        <div className={styles.formSection}>
          <label className={styles.fieldLabel}>Valor</label>
          <div className={styles.fieldInputReadonly}>
            {procedureName ? formatPrice(price) : '—'}
          </div>
        </div>

        <div className={styles.formSection}>
          <label className={styles.fieldLabel}>Status</label>
          <select
            className={styles.fieldSelect}
            value={status}
            onChange={e => setStatus(e.target.value)}
          >
            {STATUS_OPTIONS.map(opt => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* ── Recorrência (único / recorrente) ──────────────────────────────── */}
      <RecurrenceSection
        mode={mode}
        onModeChange={handleModeChange}
        intervalDays={intervalDays}
        onIntervalDaysChange={handleIntervalDaysChange}
        occurrencesCount={occurrencesCount}
        onOccurrencesCountChange={handleOccurrencesCountChange}
        occurrences={occurrences}
        onCheckAvailability={handleCheckRecurrence}
        isChecking={isCheckingRecurrence}
        error={recurrenceError}
        slot={slot}
      />

      {/* ── Toggle WhatsApp ────────────────────────────────────────────────── */}
      <div className={styles.whatsappRow}>
        <span className={styles.whatsappLabel}>
          <span className={styles.whatsappIcon}><WhatsAppIcon /></span>
          Enviar dados do agendamento por WhatsApp
        </span>
        <label className={styles.toggleWrapper}>
          <input
            type="checkbox"
            className={styles.toggleInput}
            checked={sendWhatsApp}
            onChange={e => setSendWhatsApp(e.target.checked)}
          />
          <span className={styles.toggleSlider} />
        </label>
      </div>
    </>
  );

  return (
    <>
    <div className={styles.drawerContainer} role="dialog" aria-modal="true" aria-label="Novo agendamento">

      {/* ── Header ──────────────────────────────────────────────────────────── */}
      <DrawerHeader
        slot={slot}
        title="Novo agendamento"
        isAvailable={isAvailable && !pendingConflicts.some(c => c.type === 'resource')}
        isOffProfessionalHours={!!resourceHoursWarning}
        onClose={onClose}
        duration={duration}
      />

      {/* ── Corpo do formulário ──────────────────────────────────────────────── */}
      {isRecurringMode ? (
        /* Layout de duas colunas no modo recorrente */
        <div className={`${styles.drawerBody} ${styles.drawerBodyRecurring}`}>
          {/* Coluna esquerda: formulário */}
          <div className={styles.drawerLeft}>
            {formContent}
          </div>

          {/* Coluna direita: painel de resumo */}
          <div className={styles.drawerRight}>
            <RecurrenceSummaryPanel
              slot={slot}
              intervalDays={intervalDays}
              occurrencesCount={occurrencesCount}
              occurrences={occurrences}
              onToggleSkip={handleToggleSkip}
              onSelectAlternative={handleSelectAlternative}
              isChecking={isCheckingRecurrence}
              result={recurrenceResult}
            />
          </div>
        </div>
      ) : (
        /* Layout de coluna única no modo simples */
        <div className={styles.drawerBody}>
          {formContent}
        </div>
      )}

      {/* ── Footer ──────────────────────────────────────────────────────────── */}
      <div className={styles.drawerFooterDivider} />
      <div className={styles.drawerFooter}>
        <button
          type="button"
          className={styles.btnCancel}
          onClick={rescheduleFailed ? onSaveSuccess : onClose}
          disabled={isLoading}
        >
          {recurringDone || rescheduleFailed ? 'Fechar' : 'Cancelar'}
        </button>

        {/* Após a criação em lote, o drawer permanece aberto exibindo o
            resultado. O envio da confirmação única precisa de um clique direto
            do usuário (restrição de popup do navegador). */}
        {rescheduleFailed ? null : recurringDone ? (
          pendingConfirmation ? (
            <button
              type="button"
              className={styles.btnSave}
              onClick={handleSendConfirmation}
            >
              Enviar dados por WhatsApp
            </button>
          ) : null
        ) : (
          <button
            type="button"
            className={styles.btnSave}
            onClick={isRecurringMode ? handleSaveRecurring : handleSave}
            data-tour="agenda-save"
            disabled={
              isLoading ||
              (!isRecurringMode && isCheckingUpcoming) ||
              (isRecurringMode && (!occurrences || recurrenceSummary?.toCreate === 0))
            }
          >
            {isLoading && <span className={styles.spinner} />}
            {isLoading
              ? 'Salvando...'
              : isRecurringMode
                ? `Criar ${recurrenceSummary?.toCreate || 0} agendamentos`
                : rescheduleTarget
                  ? 'Reagendar'
                  : 'Salvar agendamento'}
          </button>
        )}
      </div>
    </div>

    {/* ── Choque de horário: encaixar mesmo assim ─────────────────────────── */}
    <ConfirmDialog
      isOpen={Boolean(conflictPrompt)}
      variant="warning"
      title="Encaixar mesmo assim?"
      message="Esse horário já está ocupado:"
      confirmLabel="Encaixar mesmo assim"
      cancelLabel="Voltar"
      onConfirm={handleConflictConfirm}
      onCancel={handleConflictCancel}
    >
      <ul className={styles.conflictList}>
        {uniqueConflicts(conflictPrompt?.conflicts).map(c => (
          <li key={c.id}>{describeConflict(c)}</li>
        ))}
      </ul>
    </ConfirmDialog>

    {/* ── Popup de criação de novo paciente ───────────────────────────────── */}
    <CreatePatientPopup
      Data={null}
      SuggestedName={newPatientSuggestedName}
      Visibility={createPatientPopupVisible}
      OnHide={() => setCreatePatientPopupVisible(false)}
      OnChange={(response) => {
        const patientId = response?.data?.patientId;
        if (!patientId) return;

        // Tenta encontrar o paciente na lista já disponível
        const found = patientsRef.current?.find(p => String(p.id) === String(patientId));
        if (found) {
          setSelectedPatient(found);
          return;
        }

        // Se ainda não está na lista local (DataProvider ainda atualizando),
        // aguarda e tenta novamente usando a ref atualizada
        const retryIntervalMs = 300;
        const maxRetries = 10;
        let retries = 0;
        const interval = setInterval(() => {
          retries++;
          const updated = patientsRef.current?.find(p => String(p.id) === String(patientId));
          if (updated) {
            setSelectedPatient(updated);
            clearInterval(interval);
          } else if (retries >= maxRetries) {
            clearInterval(interval);
          }
        }, retryIntervalMs);
      }}
    />
    </>
  );
}
